/**
 * Device classification — the normalized shape every consumer reads from.
 *
 * Raw `Device` documents carry dozens of overlapping signals (`os`,
 * `osVersion`, `manufacturer`, `model`, `platform`, `typeBuild`, `serialNo`,
 * `feature.esperId`, `feature.typeBuild`, …) with inconsistent casing,
 * embedded firmware strings, and scio1-era drift. Every UI that renders a
 * device or every backend path that gates a command used to re-derive
 * meaning from those raw fields, leading to the 3-way drift this whole
 * effort is fixing (see research/devices-compatibility-matrix-maintenance-
 * best-practices.md §1, §3).
 *
 * `DeviceClassification` is the one answer. Produced ONCE by the classifier
 * in open-api (`src/app/device/device-classification.util.ts`), exposed as
 * an additive `@ResolveField` on the GraphQL `Device` type, consumed by:
 *   - device-labels.ts (display strings)
 *   - device-capability.ts (command predicates)
 *   - open-api MdmCapabilityGuard (command validation)
 *   - staff.optisigns.com cross-tenant analytics
 *   - future: fleet-level overrides, min-player-version gating
 *
 * This file defines TYPES ONLY. The classifier logic lives in open-api
 * because (a) the raw device fields are authoritative there, (b) we want
 * one implementation that runs once per device read, and (c) open-api and
 * scio3 can't share source — a CI parity check diffs the enums + catalog
 * on every PR (see scripts/check-catalog-parity.sh in open-api).
 *
 * Spec: docs/specs/19-optiedge-mdm/research/
 *       devices-compatibility-matrix-maintenance-best-practices.md §5, §5.6
 */

// ---------------------------------------------------------------------------
// Platform — top-level OS bucket
// ---------------------------------------------------------------------------
//
// Maps the scio2 `os` field to a normalized kebab-case identifier. Missing
// from scio2's OS list but present in prod (per BQ profile 2026-04-23):
// `brightsign`, `chrome-os`, `ios`, `ipados`, `unknown`. The data quirk
// `Apple TVOS` (15 devices with weird casing) normalizes to `tvos`.

export const DevicePlatform = {
  ANDROID: 'android',
  LINUX: 'linux',
  WINDOWS: 'windows',
  MAC: 'mac',
  TIZEN: 'tizen',
  WEBOS: 'webos',
  TVOS: 'tvos',
  IOS: 'ios',
  IPADOS: 'ipados',
  ROKU: 'roku',
  BRIGHTSIGN: 'brightsign',
  CHROME_OS: 'chrome-os',
  UNKNOWN: 'unknown',
} as const;
export type DevicePlatformValue = (typeof DevicePlatform)[keyof typeof DevicePlatform];

// ---------------------------------------------------------------------------
// Vendor — who made the device
// ---------------------------------------------------------------------------
//
// Normalized from the raw `manufacturer` field (BQ Q3 shows ~80 distinct
// values). Only vendors the UI treats specially get an enum value; the
// long tail falls through to `UNKNOWN` with the raw string preserved on
// `manufacturerRaw` for display.

export const DeviceVendor = {
  OPTISIGNS: 'optisigns',
  AMAZON: 'amazon',
  GOOGLE: 'google',
  APPLE: 'apple',
  SAMSUNG: 'samsung',
  LG: 'lg',
  SONY: 'sony',
  TCL: 'tcl',
  HISENSE: 'hisense',
  XIAOMI: 'xiaomi',
  ROKU: 'roku',
  BRIGHTSIGN: 'brightsign',
  ROCKCHIP: 'rockchip',
  DROIDLOGIC: 'droidlogic',
  AMLOGIC: 'amlogic',
  ALLWINNER: 'allwinner',
  TPV: 'tpv',
  SEI: 'sei',
  UNKNOWN: 'unknown',
} as const;
export type DeviceVendorValue = (typeof DeviceVendor)[keyof typeof DeviceVendor];

// ---------------------------------------------------------------------------
// Hardware family — the capability + display bucket
// ---------------------------------------------------------------------------
//
// Two tiers:
//   1. OptiSigns-manufactured hardware (we built it).
//      Keys use the `OPTISIGNS_` prefix; the namespace is meaningful here.
//   2. Third-party hardware running the OptiSigns player.
//      Keys name the hardware by its maker (RASPBERRY_PI, AMAZON_FIRE_TV,
//      WEBOS_SIGNAGE, etc.) — no `OPTISIGNS_` prefix because we did not
//      make the device.
//
// All fields are BQ-observed unless noted. See research §5.6 for the
// translation from scio2's `DEVICE_MDM_COMMANDS` buckets.
//
// OPTISTICK SUBVARIANTS (decision locked 2026-04-23, Phase A review)
// ──────────────────────────────────────────────────────────────────
// `labels.ts` renders three distinct strings for OptiStick (Faraday / SEI /
// Gen 4) but capability treats them identically. Per spec 04 §12, we keep a
// single `OPTISIGNS_ANDROID` bucket and have `labels.ts` switch on
// `(hardwareFamily, modelCode, platformCode)` for display nuance. The three
// display-only variant strings in `labels.ts` (`optisigns-optistick-faraday`,
// `optisigns-optistick-sei`, `optisigns-optistick-gen4`) are reachable via the
// `formatModel` switch even though they are not enum values here.

export const DeviceHardwareFamily = {
  // === Tier 1 — OptiSigns-manufactured hardware ===

  /** OptiStick family (OptiStick, OptiStick Gen 4, SEI players). Scio2
   *  single bucket; display-time split by modelCode (see reconciliation
   *  note above). */
  OPTISIGNS_ANDROID: 'optisigns-android',

  /** Latest-generation ProPlayer on Linux 6.12+ */
  OPTISIGNS_PRO_GEN3: 'optisigns-pro-gen3',
  /** ProPlayer Max (top-of-line electron) */
  OPTISIGNS_PRO_MAX: 'optisigns-pro-max',
  /** Older ProPlayer on electron osVersion 6.8.x */
  OPTISIGNS_PRO_GEN2: 'optisigns-pro-gen2',
  /** Ubuntu-based ProPlayer (model=optisigns_x64) — BQ-observed */
  OPTISIGNS_PRO_UBUNTU: 'optisigns-pro-ubuntu',
  /** ProPlayer Athena build (manufacturer=athena) — BQ-observed */
  OPTISIGNS_PRO_ATHENA: 'optisigns-pro-athena',

  /** OptiKiosk 101 / 321 / future SKUs — BQ-observed */
  OPTISIGNS_OPTIKIOSK: 'optisigns-optikiosk',

  // === Tier 2 — Third-party hardware running OptiSigns player ===

  // ── Single-board / compute boxes ──
  /** Raspberry Pi (linux + arm/arm64) */
  RASPBERRY_PI: 'raspberry-pi',

  // ── Named consumer OSes running our player ──
  /** Windows PC — generic Windows hardware */
  WINDOWS_PC: 'windows-pc',
  /** macOS — Apple Mac */
  MACOS: 'macos',
  /** Chrome OS / Chromebook */
  CHROME_OS: 'chrome-os',
  /** Generic Linux (non-RPi, non-ProPlayer) */
  LINUX_GENERIC: 'linux-generic',

  // ── Apple consumer ──
  /** Apple TV (tvOS) */
  APPLE_TV: 'apple-tv',
  /** iPhone (iOS) */
  IPHONE: 'iphone',
  /** iPad (iPadOS) */
  IPAD: 'ipad',

  // ── TV / signage platforms with vendor MDM ──
  /** LG webOS consumer / home TVs (limited remote support). Scio2 legacy
   *  bucket was OPTISIGNS_WEBOS. */
  WEBOS_CONSUMER: 'webos-consumer',
  /** LG webOS signage with built-in MDM (device === 'webos-signage').
   *  Scio2 legacy bucket was BUILTIN_MDM_ELECTRONS. */
  WEBOS_SIGNAGE: 'webos-signage',
  /** LG webOS signage — Athena variant (device === 'webos-signage-athena'). */
  WEBOS_SIGNAGE_ATHENA: 'webos-signage-athena',

  /** Samsung SSSP (Smart Signage Platform) */
  SAMSUNG_SSSP: 'samsung-sssp',
  /** Samsung consumer smart TVs (tizen, non-SSSP) */
  SAMSUNG_CONSUMER_TV: 'samsung-consumer-tv',

  /** BrightSign media players */
  BRIGHTSIGN: 'brightsign',

  /** Roku stick / box / TV */
  ROKU: 'roku',

  // ── Third-party Android ecosystem ──
  /** Amazon Fire TV — Stick / TV / Omni / Edition. Scio2 legacy name was
   *  OTHER_ANDROID_FIRESTICK ("firestick" is only one SKU; the family
   *  covers all Amazon Android devices running the Fire OS fork). */
  AMAZON_FIRE_TV: 'other-android-firestick',
  /** Google Chromecast / Google TV Streamer — BQ-observed */
  CHROMECAST_GOOGLE_TV: 'chromecast-google-tv',
  /** Third-party Android smart TVs (TCL/Sony/Hisense/Xiaomi/…) — BQ-observed */
  SMART_TV_ANDROID: 'smart-tv-android',
  /** Third-party Android boxes (rockchip/Droidlogic/Amlogic/Allwinner/…) —
   *  BQ-observed */
  OEM_ANDROID_BOX: 'oem-android-box',
  /** Fallback: Android device with no richer classification */
  OTHER_ANDROID: 'other-android',

  /** Third-party electron players (non-OptiSigns-branded). Scio2 legacy
   *  bucket was OTHER_ELECTRONS (plural). */
  OTHER_ELECTRON: 'other-electron',

  // === Tier 2b — Third-party conference-room systems (cloud-managed) ===
  //
  // Room-system devices are not players — they're hardware our MDM *observes*
  // via a third-party cloud API (Zoom Rooms Content API, Microsoft Graph,
  // Webex Rooms Cloud Manager, Google Meet hardware management). Management
  // capability = PLATFORM_API (not a utils-mdm heartbeat).
  //
  // Phase A ships Zoom Rooms as the first impl; Teams / Webex / Meet are
  // declared here so the capability catalog predicates + labels map can be
  // written once and Phase B just fills in adapter implementations.

  /** Zoom Rooms — hardware managed via Zoom Content API. Phase A implemented. */
  ZOOM_ROOM: 'zoom-room',
  /** Microsoft Teams Rooms (Windows) — hardware managed via Microsoft Graph
   *  `teamworkDevice` resource. Maps to deviceType `'teamsRoom'`. Phase A
   *  (Teams). */
  TEAMS_ROOM: 'teams-room',
  /** Microsoft Teams Collaboration Bar (all-in-one room bar — Logitech Rally
   *  Bar / Poly Studio X / Neat Bar etc., when running Teams firmware). Maps
   *  to Graph deviceType `'collaborationBar'`. Phase A (Teams). */
  TEAMS_COLLAB_BAR: 'teams-collab-bar',
  /** Microsoft Surface Hub — hardware managed via Microsoft Graph. Maps to
   *  Graph deviceType `'surfaceHub'`. Phase A surfaces Surface Hub 3 only;
   *  Surface Hub 2S is hardware-EOL. */
  TEAMS_SURFACE_HUB: 'teams-surface-hub',
  /** Cisco Webex Rooms — codec-driven room hardware (Room Kit, Room Bar,
   *  Codec Pro, Room 55/70/Panorama). Managed via Webex Device Configurations
   *  API. Phase A (Webex). */
  WEBEX_ROOM: 'webex-room',
  /** Cisco Webex Board — interactive whiteboard hardware (Board 55 / 70 /
   *  Pro G2). Same Webex Device Configurations API as `WEBEX_ROOM`; carved
   *  out so labels + future capability rules can split touch-first hardware
   *  from codec-driven rooms. Phase A (Webex). */
  WEBEX_BOARD: 'webex-board',
  /** Cisco Webex Desk — desk-top all-in-one hardware (Desk Pro / Desk Mini /
   *  Desk Hub). Same Webex Device Configurations API as `WEBEX_ROOM`; carved
   *  out so labels can render the desk SKU and future capability rules can
   *  account for the smaller idle-display surface. Phase A (Webex). */
  WEBEX_DESK: 'webex-desk',
  /** Google Meet hardware — managed via Google Meet hardware API. Phase B. */
  GOOGLE_MEET_ROOM: 'google-meet-room',

  // === Tier 3 — Unknown / unclassified ===
  UNKNOWN: 'unknown',
} as const;
export type DeviceHardwareFamilyValue =
  (typeof DeviceHardwareFamily)[keyof typeof DeviceHardwareFamily];

// ---------------------------------------------------------------------------
// Management capability — what MDM surface the device exposes
// ---------------------------------------------------------------------------
//
// Derived from scio2's `isAdvancedOptionAvailable` logic:
//   - `utils-mdm`    : device has `feature.esperId` OR `utilsVersion` OR is
//                      a ProPlayer or RPi (always-on utils-mdm in scio2)
//   - `builtin-mdm`  : device === 'webos-signage' or 'webos-signage-athena'
//                      (LG ships its own MDM stack, we plumb through it)
//   - `none`         : matrix-only device, no remote-command surface

export const ManagementCapability = {
  UTILS_MDM: 'utils-mdm',
  BUILTIN_MDM: 'builtin-mdm',
  /** Cloud-API managed — no device-side heartbeat. Used by third-party
   *  room-system hardware (Zoom Rooms, Teams Rooms, Webex Rooms, Google
   *  Meet). Commands dispatch through the vendor cloud, not the player. */
  PLATFORM_API: 'platform-api',
  NONE: 'none',
} as const;
export type ManagementCapabilityValue =
  (typeof ManagementCapability)[keyof typeof ManagementCapability];

// ---------------------------------------------------------------------------
// The classification itself
// ---------------------------------------------------------------------------
//
// Every field is required. The classifier always emits SOMETHING for every
// field (even if that's `UNKNOWN`, `'—'`, or `null`) so downstream readers
// never deal with partial data.
//
// Additive fields are safe; removing / renaming fields is a breaking change
// that needs coordinated open-api + scio3 deploys.

export interface DeviceClassification {
  /** OS bucket. */
  platform: DevicePlatformValue;

  /** Who made it. `UNKNOWN` + `manufacturerRaw` when the vendor is a
   *  long-tail string not in the enum. */
  vendor: DeviceVendorValue;

  /** Capability + display bucket. See DeviceHardwareFamily for the taxonomy. */
  hardwareFamily: DeviceHardwareFamilyValue;

  /** What MDM surface the device exposes. */
  managementCapability: ManagementCapabilityValue;

  /** Cleaned, human-ready version string.
   *
   *  Classifier responsibilities (NOT delegated to labels.ts):
   *    - "6.1.99-factory"       → "6"          (strip suffixes)
   *    - "windows-10"           → "10"         (strip OS-name prefix)
   *    - "mac-os-x-15"          → "15"         (strip OS-name prefix)
   *    - "T-KSU2EC...Tizen 8.0" → "8.0"        (extract from firmware string)
   *    - ProPlayer kernel       → ""           (blank — kernel not user-facing)
   *    - "unknown" (Chrome OS)  → ""           (blank)
   *    - "9.0.168" (BrightSign) → "9"          (major only)
   *
   *  `""` means "known to be absent / not worth rendering" — labels.ts treats
   *  empty version as "suppress version suffix". */
  osVersionDisplay: string;

  /** Numeric major version for predicate comparison (future minPlayerVersion
   *  enforcement — see research §5.5). `null` if unparseable or absent. */
  osVersionMajor: number | null;

  /** Raw `model` string from the device doc. Preserved so labels.ts can
   *  (a) resolve Fire TV codes via AMAZON_FIRE_TV_MODELS, (b) render the
   *  OptiKiosk SKU suffix ("OptiKiosk 101"), (c) render the unknown-family
   *  fallback. `null` if absent. */
  modelCode: string | null;

  /** Raw `manufacturer` string from the device doc. Used as display fallback
   *  when `vendor === 'unknown'` so support can see what the device is
   *  actually reporting. `null` if absent. */
  manufacturerRaw: string | null;

  /** Raw `device.platform` board / SoC identifier (e.g. `SEI700D2OTS`,
   *  `SEI900D2OTS`, `faraday`, `rk3576`). Distinct from the normalized OS
   *  `platform` field above — this is the hardware board reported by the
   *  player, used by `labels.ts` to distinguish OptiStick Gen 2 / 3 / 4 which
   *  share the same `modelCode`. `null` if absent. */
  platformRaw: string | null;
}

// ---------------------------------------------------------------------------
// Convenience predicate helpers (used by device-capability.ts and tests)
// ---------------------------------------------------------------------------
//
// Composable checks published so consumers (capability predicates, fleet-
// level overrides, staff diagnostics) can build predicates without
// duplicating family lists.

/** True for any OptiSigns-made ProPlayer (electron) variant. */
export function isOptiSignsPro(c: DeviceClassification): boolean {
  return (
    c.hardwareFamily === DeviceHardwareFamily.OPTISIGNS_PRO_GEN3 ||
    c.hardwareFamily === DeviceHardwareFamily.OPTISIGNS_PRO_MAX ||
    c.hardwareFamily === DeviceHardwareFamily.OPTISIGNS_PRO_GEN2 ||
    c.hardwareFamily === DeviceHardwareFamily.OPTISIGNS_PRO_UBUNTU ||
    c.hardwareFamily === DeviceHardwareFamily.OPTISIGNS_PRO_ATHENA
  );
}

/** True for any OptiSigns-made hardware (ProPlayer, OptiStick, OptiKiosk). */
export function isOptiSignsHardware(c: DeviceClassification): boolean {
  return (
    isOptiSignsPro(c) ||
    c.hardwareFamily === DeviceHardwareFamily.OPTISIGNS_ANDROID ||
    c.hardwareFamily === DeviceHardwareFamily.OPTISIGNS_OPTIKIOSK
  );
}

/** True for any LG webOS variant (consumer, signage, signage-athena). */
export function isWebOsFamily(c: DeviceClassification): boolean {
  return (
    c.hardwareFamily === DeviceHardwareFamily.WEBOS_CONSUMER ||
    c.hardwareFamily === DeviceHardwareFamily.WEBOS_SIGNAGE ||
    c.hardwareFamily === DeviceHardwareFamily.WEBOS_SIGNAGE_ATHENA
  );
}

/** True for LG webOS signage variants with built-in MDM (signage + athena).
 *  Excludes consumer webOS TVs. Used for "which devices have LG's own MDM
 *  stack so we shouldn't push our own timezone/info commands". */
export function isWebOsSignage(c: DeviceClassification): boolean {
  return (
    c.hardwareFamily === DeviceHardwareFamily.WEBOS_SIGNAGE ||
    c.hardwareFamily === DeviceHardwareFamily.WEBOS_SIGNAGE_ATHENA
  );
}

/** True for devices with any MDM surface (utils-mdm, builtin-mdm, or platform-api). */
export function hasManagementCapability(c: DeviceClassification): boolean {
  return c.managementCapability !== ManagementCapability.NONE;
}

/** True for third-party conference-room systems (Zoom / Teams / Webex / Meet).
 *  These share `managementCapability === 'platform-api'` — commands dispatch
 *  through a vendor cloud API rather than a utils-mdm heartbeat. */
export function isRoomSystem(c: DeviceClassification): boolean {
  return (
    c.hardwareFamily === DeviceHardwareFamily.ZOOM_ROOM ||
    c.hardwareFamily === DeviceHardwareFamily.TEAMS_ROOM ||
    c.hardwareFamily === DeviceHardwareFamily.TEAMS_COLLAB_BAR ||
    c.hardwareFamily === DeviceHardwareFamily.TEAMS_SURFACE_HUB ||
    c.hardwareFamily === DeviceHardwareFamily.WEBEX_ROOM ||
    c.hardwareFamily === DeviceHardwareFamily.WEBEX_BOARD ||
    c.hardwareFamily === DeviceHardwareFamily.WEBEX_DESK ||
    c.hardwareFamily === DeviceHardwareFamily.GOOGLE_MEET_ROOM
  );
}

/** True for Microsoft Teams-family room hardware. All 3 are signage-eligible
 *  per Microsoft (require Teams Rooms Pro license); the rest of Microsoft's
 *  `teamworkDevice` deviceTypes (`teamsPanel`, `teamsDisplay`, `touchConsole`,
 *  `ipPhone`, `sip`, `sipAnalog`) are filtered out by the Teams adapter
 *  mapper before they ever land in `mdm-devices`. */
export function isTeamsRoomFamily(c: DeviceClassification): boolean {
  return (
    c.hardwareFamily === DeviceHardwareFamily.TEAMS_ROOM ||
    c.hardwareFamily === DeviceHardwareFamily.TEAMS_COLLAB_BAR ||
    c.hardwareFamily === DeviceHardwareFamily.TEAMS_SURFACE_HUB
  );
}

/** True for Cisco Webex room hardware (Webex Room / Board / Desk). All 3
 *  share the same Webex Device Configurations API (`Standby.Signage.*`) and
 *  the same OptiSigns adapter implementation — split only for display + future
 *  capability nuance. Takes the family value directly (not a full
 *  classification) per spec 01 §3.1 — call site is the drawer mount in
 *  `apps/web` which only has `device.hardwareFamily` in scope. */
export function isWebexFamily(family: DeviceHardwareFamilyValue): boolean {
  return (
    family === DeviceHardwareFamily.WEBEX_ROOM ||
    family === DeviceHardwareFamily.WEBEX_BOARD ||
    family === DeviceHardwareFamily.WEBEX_DESK
  );
}
